import { useState, useEffect, useCallback } from 'react';
import {
  ManagedUser,
  CreateUserPayload,
  UpdateUserPayload,
} from '../types/user';
import {
  getAllUsers,
  getAllRoles,
  createUser,
  updateUser,
  deleteUser,
} from '../services/userService';
import { useAlert } from './useAlert';
import { useAdminGuard } from './useAdminGuard';
import { extractApiError } from '../utils/errorUtils';
import { PaginationParams } from '../types/pagination';

const DEFAULT_LIMIT = 20;

export function useUsers() {
  const { isAdmin, currentUser } = useAdminGuard();
  const { alert, showAlert, hideAlert } = useAlert();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [roles, setRoles] = useState<{ id: number; role_name: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    hideAlert();
    try {
      const params: PaginationParams = { page, limit, search: search || undefined, sortBy, sortOrder };
      const result = await getAllUsers(params);
      setUsers(result.items);
      setTotal(result.total);
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to load users.'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder, hideAlert, showAlert]);

  const fetchRoles = useCallback(async () => {
    try {
      const result = await getAllRoles({ limit: 100 });
      setRoles(result.items.map((r) => ({ id: r.id, role_name: r.role_name })));
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to load roles.'), 'error');
    }
  }, [showAlert]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchRoles();
    }
  }, [isAdmin, fetchUsers, fetchRoles]);

  const handleAddUser = useCallback(() => {
    setEditingUser(null);
    setIsModalOpen(true);
    hideAlert();
  }, [hideAlert]);

  const handleEditUser = useCallback((user: ManagedUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
    hideAlert();
  }, [hideAlert]);

  const handleFormSubmit = useCallback(async (data: CreateUserPayload | UpdateUserPayload) => {
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingUser) {
        const payload = data as UpdateUserPayload;
        const response = await updateUser(editingUser.id, payload);
        setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? response.user : u)));
        showAlert(response.message || 'User updated successfully!', 'success');
      } else {
        const payload = data as CreateUserPayload;
        const response = await createUser(payload);
        setUsers((prev) => [response.user, ...prev]);
        showAlert(response.message || 'User created successfully!', 'success');
      }
      setIsModalOpen(false);
      setEditingUser(null);
    } catch (err) {
      showAlert(extractApiError(err, editingUser ? 'Failed to update user.' : 'Failed to create user.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingUser, hideAlert, showAlert]);

  const handleDeleteUser = useCallback(async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This will permanently remove the user and their associated data (stores, reviews, etc.).')) {
      return;
    }
    hideAlert();
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setTotal((t) => Math.max(0, t - 1));
      showAlert('User deleted successfully.', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete user.'), 'error');
    }
  }, [hideAlert, showAlert]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
  }, []);

  const totalPages = Math.ceil(total / limit) || 1;

  return {
    isAdmin,
    currentUser,
    users,
    roles,
    isLoading,
    isSubmitting,
    isModalOpen,
    editingUser,
    alert,
    showAlert,
    hideAlert,
    fetchUsers,
    handleAddUser,
    handleEditUser,
    handleFormSubmit,
    handleDeleteUser,
    closeModal,
    pagination: {
      page,
      limit,
      total,
      totalPages,
      setPage,
      setLimit,
      setSearch,
      setSortBy,
      setSortOrder,
      search,
      sortBy,
      sortOrder,
    },
  };
}
